import apiService, { ServiceType } from './api';

/**
 * The basic book: shares the broker holds that no strategy claims.
 *
 * It is a VIEW, not a portfolio (plan D11). There is no basic account, nothing was moved
 * into it and no cash-op runs against it — it is the broker's snapshot minus what the
 * strategies' wallets say they own. That is why the only write here is `sync`, which
 * re-reads the broker: the numbers are an inference, and refreshing the input is the only
 * thing anyone can do to them.
 *
 * UNITS: `cost` and `averagePrice` are RAW VND. See docs/MONEY-CONCEPTS.md.
 */

export interface BasicHolding {
    exchange: string;
    symbol: string;
    /** Shares the broker reports, minus what strategies claim. */
    quantity: number;
    /**
     * Cost of those shares, raw VND: the broker's total cost for the symbol minus what the
     * strategies claim. Null when the broker reports no cost (or the claims exceed it).
     */
    cost: number | null;
    /** `cost / quantity`, raw VND per share. Null when the cost is unknown. */
    averagePrice: number | null;
    /** When the broker read behind this row was taken (ISO). */
    capturedAt: string;
    /** Always the broker, read by a data adapter. */
    source: 'BROKER';
    /** True when the broker could not be re-read and this is its last answer. */
    stale: boolean;
}

const core = () => apiService.getService(ServiceType.CORE);

export async function getBasicBook(portfolioId: string): Promise<BasicHolding[]> {
    const { data } = await core().get<BasicHolding[]>(`/portfolios/${portfolioId}/basic-book`);
    return data;
}

/**
 * Cash no strategy claims — the cash half of the same view.
 *
 * `availableToDeposit`, not the broker balance and not purchasing power. Gross cash minus
 * what wallets already hold idle: PP would debit resting BUY orders a second time, and the
 * raw balance would count money that is already spoken for. Null when the broker is
 * unreachable, which the screen shows as "—" rather than as zero.
 */
export async function getUnclaimedCash(portfolioId: string): Promise<number | null> {
    const { data } = await core().get<{ availableToDeposit: string | null }>(
        `/instrument-config/deposit-capacity/${portfolioId}`,
    );
    return data.availableToDeposit == null ? null : Number(data.availableToDeposit);
}

/** Re-read the broker and return the recomputed view. The only write this screen has. */
export async function syncBasicBook(portfolioId: string): Promise<BasicHolding[]> {
    const { data } = await core().post<BasicHolding[]>(`/portfolios/${portfolioId}/basic-book/sync`);
    return data;
}
